import Raven from 'raven-js';

/**
 * A wrapper around the performance API, which can also send timing information
 * to Google Analytics. Provides no-op fallbacks if the performance API is not available.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/performance
 * @see https://www.html5rocks.com/en/tutorials/webperformance/usertiming/
 */
export class PerformanceService {
    constructor($window, Analytics) {
        'ngInject';

        if (this._isPerformanceApiAvailable($window)) {
            this.performance = $window.performance;
        } else {
            this.performance = new NoopPerformanceFacade();

            Raven.captureMessage('Performance API not available.', {
                level: 'info'
            });
        }

        this.Analytics = Analytics || new NoopAnalyticsFacade();
    }

    /**
     *
     * @param {*} window
     * @returns {boolean}
     */
    _isPerformanceApiAvailable(window) {
        return !!(
            window
            && window.performance
            && window.performance.now
            && window.performance.mark
            && window.performance.measure
            && window.performance.getEntriesByName
        );
    }

    /**
     * Returns a function that calls `startTiming()` with the provided `category` to
     * simplify creating new timings.
     *
     * @param {string} category
     * @returns {PerformanceService~startTimingFunction}
     */
    timingCategory(category) {
        return name => this.startTiming({ category, name });
    }

    /**
     * Starts a timing operation and returns a function that will end the timing
     * and report it to analytics. The returned function is idempotent.
     *
     * @param {object} config
     * @param {string} config.category
     * @param {string} config.name
     * @returns {PerformanceService~stopTimingFunction}
     */
    startTiming({ category, name } = {}) {
        const fullName = `${category} - ${name}`;
        const startMarkName = `${fullName} (start)`;
        let called = false;

        this.performance.mark(startMarkName);

        return ({
            sendToAnalytics = true,
            analyticsName = null,
            analyticsLabel = null,
            analyticsIncludeNavigationStart = false
        } = {}) => {
            if (called) {
                return;
            }

            const endMarkName = `${fullName} (end)`;
            this.performance.mark(endMarkName);
            this.performance.measure(fullName, startMarkName, endMarkName);

            if (sendToAnalytics) {
                // Per the performance API spec, all mark startTimes are relative to
                // performance.timing.navigationStart.
                const timeSinceNavigationStart = analyticsIncludeNavigationStart ? (this
                    ._getLastEntryByName(endMarkName, 'mark')
                    .startTime) : null;
                const duration = this
                    ._getLastEntryByName(fullName, 'measure')
                    .duration;

                this._sendTimingToAnalytics({
                    category,
                    name: analyticsName || name,
                    duration,
                    timeSinceNavigationStart,
                    label: analyticsLabel
                });
            }

            called = true;
        };
    }

    _sendTimingToAnalytics({
        category,
        name,
        duration = null,
        timeSinceNavigationStart = null,
        label = null
    } = {}) {
        if (duration) {
            this.Analytics.trackTimings(
                category,
                name,
                duration,
                label
            );
        }

        if (timeSinceNavigationStart) {
            this.Analytics.trackTimings(
                category,
                `${name} (navigationStart)`,
                timeSinceNavigationStart,
                label
            );
        }
    }

    /**
     *
     * @param {string} name
     * @param {string} type
     * @returns {PerformanceEntry}
     */
    _getLastEntryByName(name, type) {
        const entries = this.performance.getEntriesByName(name, type) || [];
        return entries.length
            ? entries[entries.length - 1]
            : new PerformanceEntryFacade();
    }
}

class NoopAnalyticsFacade {
    trackTimings() {
        // no-op
    }
}

class NoopPerformanceFacade {
    now() {
        return null;
    }

    mark() {
        // no-op
    }

    measure() {
        // no-op
    }

    getEntries() {
        return [];
    }

    getEntriesByName() {
        return [];
    }

    getEntriesByType() {
        return [];
    }
}

class PerformanceEntryFacade {
    get startTime() {
        return null;
    }

    get duration() {
        return null;
    }
}

/**
 * Starts a timing operation within the context of the category to which this function is bound.
 *
 * @function PerformanceService~startTimingFunction
 * @param {string} name the name of the timing operation
 * @returns {PerformanceService~stopTimingFunction}
 */

/**
 * @typedef {object} PerformanceService~stopTimingFunctionOptions
 * @property {boolean} sendToAnalytics `true` by default
 * @property {string} analyticsName allows using a different name when sending to analytics
 * @property {string} analyticsLabel allows passing the label arg to analytics
 * @property {boolean} analyticsIncludeNavigationStart if `true` will send an additional
 *  timing event to analytics with respect to `performance.timing.navigationStart`
 */

/**
 * Stops the timing operation to which this function is bound, reporting it to the browser's
 * Performance API as well as potentially sending it to analytics.
 *
 * @function PerformanceService~stopTimingFunction
 * @param {PerformanceService~stopTimingFunctionOptions} options
 * @returns {void}
 */
